import { ViewportScroller } from '@angular/common';
import { Directive, Input, HostBinding, HostListener, inject } from '@angular/core';

/**
 * Scrolls the page to a target anchor when the host element is clicked.
 *
 * Usage:
 * - Apply the directive on any clickable element and provide an `anchor` id.
 * - On click, it calls `ViewportScroller.scrollToAnchor(anchor)`.
 *
 * Inputs:
 * - `anchor: string` — The id of the element to scroll to (without `#`).
 *
 * Example:
 * ```html
 * <button anchor='details'>Go to details</button>
 *
 * <section id="details">...</section>
 * ```
 */
@Directive({
    selector: '[anchor]',
    standalone: true,
})
export class AnchorDirective {
    private viewportScroller = inject(ViewportScroller);

    /**
     * Target anchor id.
     */
    @Input() anchor: string;

    /**
     * Applies pointer cursor to indicate clickability.
     */
    @HostBinding('class') get elementClass() {
        return {
            ['bh-pointer']: true,
        };
    }

    /**
     * Handles click on the host element and performs scrolling.
     */
    @HostListener('click')
    public onClick() {
        this.scrollToAnchor(this.anchor);
    }

    /**
     * Scrolls to the provided anchor id.
     * @param anchor Id of the element to scroll to (without the leading `#`).
     */
    scrollToAnchor(anchor: string) {
        this.viewportScroller.scrollToAnchor(anchor);
    }
}
